import { Button } from "@components/button/button-untitledui";
import { useEffect, useState } from "react";
import { EXPEDIENT_MESSAGES } from "@/features/vehicle/constants";
import {
	getActiveScheduleRows,
	mapApiDaysToForm,
	mapFormToApiPayload,
	validateSchedule,
} from "@/features/vehicle/handlers";
import {
	useCreateOrUpdateExpedient,
	useGetExpedient,
} from "@/features/vehicle/query";
import type {
	ExpedientCardProps,
	ExpedientDayForm,
} from "@/features/vehicle/types";
import { useToast } from "@/providers/toast-provider";
import { ExpedientModal } from "./expedient-modal";

export const ExpedientCard = ({ vehicleId }: ExpedientCardProps) => {
	const { showToast } = useToast();
	const { data, isLoading } = useGetExpedient(vehicleId);
	const expedientDays = data ?? [];
	const saveMutation = useCreateOrUpdateExpedient();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [replicateToAll, setReplicateToAll] = useState(false);
	const [errorsByDay, setErrorsByDay] = useState<
		Partial<Record<number, string>>
	>({});
	const [formDays, setFormDays] = useState<ExpedientDayForm[]>(
		mapApiDaysToForm(expedientDays)
	);

	useEffect(() => {
		if (isModalOpen) {
			return;
		}
		setFormDays(mapApiDaysToForm(expedientDays));
	}, [expedientDays, isModalOpen]);

	const hasConfiguredDay = expedientDays.some((item) => item.isActive);
	const cardRows = getActiveScheduleRows(mapApiDaysToForm(expedientDays));

	const handleOpenModal = () => {
		setFormDays(mapApiDaysToForm(expedientDays));
		setErrorsByDay({});
		setReplicateToAll(false);
		setIsModalOpen(true);
	};

	const handleToggleDay = (day: number, isActive: boolean) => {
		setFormDays((previousDays) =>
			previousDays.map((item) => {
				if (item.day !== day) {
					return item;
				}
				if (!isActive) {
					return { ...item, isActive, startTime: "", endTime: "" };
				}
				return { ...item, isActive };
			})
		);
		setErrorsByDay((previousErrors) => ({
			...previousErrors,
			[day]: undefined,
		}));
	};

	const handleChangeTime = (
		day: number,
		field: "startTime" | "endTime",
		value: string
	) => {
		setFormDays((previousDays) =>
			previousDays.map((item) => {
				if (item.day !== day) {
					return item;
				}
				return { ...item, [field]: value };
			})
		);
		setErrorsByDay((previousErrors) => ({
			...previousErrors,
			[day]: undefined,
		}));
	};

	const handleSubmit = async () => {
		const validationErrors = validateSchedule(formDays);

		if (Object.keys(validationErrors).length > 0) {
			setErrorsByDay(validationErrors);
			showToast({
				variant: "error",
				...EXPEDIENT_MESSAGES.VALIDATION_ERROR,
			});
			return;
		}

		try {
			await saveMutation.mutateAsync({
				vehicleId: Number(vehicleId),
				replicateToAll,
				days: mapFormToApiPayload(formDays),
			});
			showToast({
				variant: "success",
				...EXPEDIENT_MESSAGES.UPDATE_SUCCESS,
			});
			setIsModalOpen(false);
		} catch (_error) {
			showToast({
				variant: "error",
				...EXPEDIENT_MESSAGES.UPDATE_ERROR,
			});
		}
	};

	return (
		<>
			<div className="flex flex-col gap-3 rounded-[20px] border border-secondary_alt border-solid bg-bg-secondary p-6">
				<div className="flex flex-wrap items-center justify-between gap-2">
					<div className="min-w-0">
						<h3 className="font-bold text-gray-700 text-lg leading-7">
							Expediente
						</h3>
						<p className="text-gray-600 text-xs leading-5">
							Informações relacionadas ao horário de uso.
						</p>
					</div>
					{hasConfiguredDay ? (
						<Button color="link-color" onClick={handleOpenModal} size="sm">
							Editar
						</Button>
					) : null}
				</div>

				{isLoading ? (
					<div className="min-h-16 py-2 text-fg-secondary text-sm">
						Carregando...
					</div>
				) : null}

				{isLoading || hasConfiguredDay ? null : (
					<div>
						<Button color="primary" onClick={handleOpenModal} size="sm">
							Adicionar horário
						</Button>
					</div>
				)}

				{!isLoading && hasConfiguredDay ? (
					<div className="flex w-full gap-4">
						<div className="flex w-full max-w-[258px] flex-col gap-1">
							<p className="font-normal text-gray-700 text-sm leading-5">
								Dias da semana
							</p>
							{cardRows.map((row) => (
								<p
									className="font-bold text-gray-900 text-md leading-6"
									key={`day-${row.day}`}
								>
									{row.day}
								</p>
							))}
						</div>
						<div className="flex w-full max-w-[175px] flex-col gap-1">
							<p className="font-normal text-gray-700 text-sm leading-5">
								Horário
							</p>
							{cardRows.map((row) => (
								<p
									className="font-bold text-gray-900 text-md leading-6"
									key={`schedule-${row.day}`}
								>
									{row.schedule}
								</p>
							))}
						</div>
					</div>
				) : null}
			</div>

			<ExpedientModal
				days={formDays}
				errorsByDay={errorsByDay}
				isOpen={isModalOpen}
				isSaving={saveMutation.isPending}
				onChangeReplicateToAll={setReplicateToAll}
				onChangeTime={handleChangeTime}
				onClose={() => setIsModalOpen(false)}
				onSubmit={handleSubmit}
				onToggleDay={handleToggleDay}
				replicateToAll={replicateToAll}
			/>
		</>
	);
};
